import { useState, useCallback } from "react";
import axios from "axios";

interface ApiErrorHandlingOptions {
  defaultErrorMessage?: string;
}

/**
 * Standard API error structure from server
 */
interface ApiError {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  timestamp?: string;
  errors?: { [field: string]: string[] };
  [key: string]: unknown;
}

interface ApiErrorHandlingReturn {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] }; // Used only for client-side validation
  setApiErrors: (errors: string[]) => void;
  setFieldErrors: (errors: { [k: string]: string[] }) => void;
  clearErrors: () => void;
  handleApiError: (error: unknown) => void;
}

const extractErrors = (
  data: ApiError,
  defaultErrorMessage: string,
  setApiErrors: (errors: string[]) => void,
  setFieldErrors: (errors: { [k: string]: string[] }) => void,
) => {
  // If server returned field-specific errors — use them
  if (data.errors && typeof data.errors === "object") {
    const fieldErrs = data.errors as { [field: string]: string[] };
    setFieldErrors(fieldErrs);
    // Collect all field error messages for the error container
    const messages = Object.values(fieldErrs).flat();
    setApiErrors(
      messages.length > 0 ? messages : [data.title || defaultErrorMessage],
    );
    return;
  }

  // No field errors — show detail or title
  const message = data.detail || data.title || defaultErrorMessage;
  setApiErrors([message]);
};

/**
 * Hook for handling API errors with automatic message extraction
 *
 * Server errors are extracted from the new API error structure:
 * - If `errors` is present: field errors are extracted and shown in the error container,
 *   fields are highlighted via fieldErrors
 * - Fallback priority: detail > title > default message
 *
 * @param options - Configuration options
 * @returns Error handling utilities
 */
export const useApiErrorHandling = (
  options: ApiErrorHandlingOptions = {},
): ApiErrorHandlingReturn => {
  const { defaultErrorMessage = "An error occurred. Please try again." } =
    options;

  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<{ [k: string]: string[] }>({});

  const clearErrors = useCallback(() => {
    setApiErrors([]);
    setFieldErrors({});
  }, []);

  const handleApiError = useCallback(
    (error: unknown) => {
      console.log("API Error:", error);

      // Check if it's already an ApiError object (from unwrap())
      if (
        error &&
        typeof error === "object" &&
        ("detail" in error || "title" in error || "errors" in error)
      ) {
        extractErrors(
          error as ApiError,
          defaultErrorMessage,
          setApiErrors,
          setFieldErrors,
        );
        return;
      }

      // Extract error message from axios error response
      if (axios.isAxiosError(error) && error.response?.data) {
        extractErrors(
          error.response.data as ApiError,
          defaultErrorMessage,
          setApiErrors,
          setFieldErrors,
        );
        return;
      }

      // Handle plain objects with message property
      if (error && typeof error === "object" && "message" in error) {
        setApiErrors([String((error as { message: unknown }).message)]);
        return;
      }

      // Fallback to default error message
      setApiErrors([defaultErrorMessage]);
    },
    [defaultErrorMessage],
  );

  return {
    apiErrors,
    fieldErrors, // Kept for client-side validation compatibility
    setApiErrors,
    setFieldErrors,
    clearErrors,
    handleApiError,
  };
};
